import apiClient from './api';
import { API_ENDPOINTS } from '@/config/api.config';
import type {
  Review,
  ReviewStats,
  ReviewListResponse,
  CreateReviewRequest,
  UpdateReviewRequest,
  UpdateReviewStatusRequest,
  AddAdminResponseRequest,
  ReportReviewRequest,
  ReviewListParams,
  AdminReviewListParams,
  ToggleFeaturedRequest,
  ApiResponse,
  MarkHelpfulResponse,
  AdminDashboardMetrics,
  BulkReviewStatsResponse,
} from '@/types/review.types';
import { ReviewType } from '@/types/review.types';


// Error Handler


interface ApiErrorResponse {
  response?: {
    data?: {
      message?: string;
      error:{
          error_description?:string;
      }
    };
  };
}

function handleError(error: unknown, defaultMessage: string): never {
  const apiError = error as ApiErrorResponse;
  const message = apiError.response?.data?.message || apiError.response?.data?.error?.error_description || defaultMessage;
  throw new Error(message);
}


// Review Service


class ReviewService {

  // Learner Endpoints


  /**
   * Create a new review
   * POST /api/reviews/learner
   */
  async createReview(data: CreateReviewRequest): Promise<Review> {
    try {
      const response = await apiClient.post<ApiResponse<Review>>(
        API_ENDPOINTS.REVIEWS.LEARNER_CREATE,
        data
      );
      return response.data.data;
    } catch (error) {
      handleError(error, 'Failed to create review');
    }
  }

  /**
   * Get current user's reviews
   * GET /api/reviews/learner/my-reviews
   */
  async getMyReviews(): Promise<ReviewListResponse> {
    try {
      const response = await apiClient.get<ApiResponse<ReviewListResponse>>(
        API_ENDPOINTS.REVIEWS.LEARNER_MY_REVIEWS
      );
      return response.data.data;
    } catch (error) {
      handleError(error, 'Failed to fetch my reviews');
    }
  }

  /**
   * Update own review
   * PUT /api/reviews/learner/:review_id
   */
  async updateReview(reviewId: string, data: UpdateReviewRequest): Promise<Review> {
    try {
      const response = await apiClient.put<ApiResponse<Review>>(
        API_ENDPOINTS.REVIEWS.LEARNER_UPDATE(reviewId),
        data
      );
      return response.data.data;
    } catch (error) {
      handleError(error, 'Failed to update review');
    }
  }

  /**
   * Delete own review
   * DELETE /api/reviews/learner/:review_id
   */
  async deleteReview(reviewId: string): Promise<void> {
    try {
      await apiClient.delete(API_ENDPOINTS.REVIEWS.LEARNER_DELETE(reviewId));
    } catch (error) {
      handleError(error, 'Failed to delete review');
    }
  }

  /**
   * Mark a review as helpful (toggle)
   * POST /api/reviews/learner/:review_id/helpful
   */
  async markHelpful(reviewId: string): Promise<MarkHelpfulResponse> {
    try {
      const response = await apiClient.post<ApiResponse<MarkHelpfulResponse>>(
        API_ENDPOINTS.REVIEWS.LEARNER_HELPFUL(reviewId)
      );
      return response.data.data;
    } catch (error) {
      handleError(error, 'Failed to mark review as helpful');
    }
  }

  /**
   * Report a review
   * POST /api/reviews/learner/:review_id/report
   */
  async reportReview(reviewId: string, data: ReportReviewRequest): Promise<void> {
    try {
      await apiClient.post(API_ENDPOINTS.REVIEWS.LEARNER_REPORT(reviewId), data);
    } catch (error) {
      handleError(error, 'Failed to report review');
    }
  }


  // Admin Endpoints


  /**
   * Get all reviews (admin)
   * GET /api/reviews/admin
   */
  async getAdminReviews(params: AdminReviewListParams = {}): Promise<ReviewListResponse> {
    try {
      const response = await apiClient.get<ApiResponse<ReviewListResponse>>(
        API_ENDPOINTS.REVIEWS.ADMIN_LIST,
        { params }
      );
      return response.data.data;
    } catch (error) {
      handleError(error, 'Failed to fetch reviews');
    }
  }

  /**
   * Get admin dashboard metrics
   * GET /api/reviews/admin/metrics
   */
  async getAdminDashboardMetrics(): Promise<AdminDashboardMetrics> {
    try {
      const response = await apiClient.get<ApiResponse<AdminDashboardMetrics>>(
        API_ENDPOINTS.REVIEWS.ADMIN_METRICS
      );
      return response.data.data;
    } catch (error) {
      handleError(error, 'Failed to fetch metrics');
    }
  }

  /**
   * Get single review by ID (admin)
   * GET /api/reviews/admin/:review_id
   */
  async getAdminReviewById(reviewId: string): Promise<Review> {
    try {
      const response = await apiClient.get<ApiResponse<Review>>(
        API_ENDPOINTS.REVIEWS.ADMIN_GET(reviewId)
      );
      return response.data.data;
    } catch (error) {
      handleError(error, 'Failed to fetch review');
    }
  }

  /**
   * Update review status (admin)
   * PUT /api/reviews/admin/:review_id/status
   */
  async updateReviewStatus(
    reviewId: string,
    data: UpdateReviewStatusRequest
  ): Promise<Review> {
    try {
      const response = await apiClient.put<ApiResponse<Review>>(
        API_ENDPOINTS.REVIEWS.ADMIN_UPDATE_STATUS(reviewId),
        data
      );
      return response.data.data;
    } catch (error) {
      handleError(error, 'Failed to update review status');
    }
  }

  /**
   * Add admin response to review
   * POST /api/reviews/admin/:review_id/response
   */
  async addAdminResponse(
    reviewId: string,
    data: AddAdminResponseRequest
  ): Promise<Review> {
    try {
      const response = await apiClient.post<ApiResponse<Review>>(
        API_ENDPOINTS.REVIEWS.ADMIN_RESPONSE(reviewId),
        data
      );
      return response.data.data;
    } catch (error) {
      handleError(error, 'Failed to add response');
    }
  }

  /**
   * Toggle featured status (admin)
   * PUT /api/reviews/admin/:review_id/feature
   */
  async toggleFeatured(reviewId: string, data: ToggleFeaturedRequest): Promise<Review> {
    try {
      const response = await apiClient.put<ApiResponse<Review>>(
        API_ENDPOINTS.REVIEWS.ADMIN_FEATURE(reviewId),
        data
      );
      return response.data.data;
    } catch (error) {
      handleError(error, 'Failed to update featured status');
    }
  }

  /**
   * Delete review (admin)
   * DELETE /api/reviews/admin/:review_id
   */
  async adminDeleteReview(reviewId: string): Promise<void> {
    try {
      await apiClient.delete(API_ENDPOINTS.REVIEWS.ADMIN_DELETE(reviewId));
    } catch (error) {
      handleError(error, 'Failed to delete review');
    }
  }

  /**
   * Get course reviews for admin (includes flagged/deleted)
   * GET /api/reviews/admin/course/:courseId/reviews
   * Supports filters: status, rating, is_deleted
   */
  async getAdminCourseReviews(
    courseId: string,
    params: AdminReviewListParams = {}
  ): Promise<ReviewListResponse> {
    try {
      const response = await apiClient.get<ApiResponse<ReviewListResponse>>(
        API_ENDPOINTS.REVIEWS.ADMIN_COURSE_REVIEWS(courseId),
        { params }
      );
      return response.data.data;
    } catch (error) {
      handleError(error, 'Failed to fetch course reviews');
    }
  }

  /**
   * Get certification reviews for admin (includes flagged/deleted)
   * GET /api/reviews/admin/certification/:certificationId/reviews
   * Supports filters: status, rating, is_deleted
   */
  async getAdminCertificationReviews(
    certificationId: string,
    params: AdminReviewListParams = {}
  ): Promise<ReviewListResponse> {
    try {
      const response = await apiClient.get<ApiResponse<ReviewListResponse>>(
        API_ENDPOINTS.REVIEWS.ADMIN_CERTIFICATION_REVIEWS(certificationId),
        { params }
      );
      return response.data.data;
    } catch (error) {
      handleError(error, 'Failed to fetch certification reviews');
    }
  }

  /**
   * Get learning path reviews for admin (includes flagged/deleted)
   * GET /api/reviews/admin/learning-path/:learningPathId/reviews
   * Supports filters: status, rating, is_deleted
   */
  async getAdminLearningPathReviews(
    learningPathId: string,
    params: AdminReviewListParams = {}
  ): Promise<ReviewListResponse> {
    try {
      const response = await apiClient.get<ApiResponse<ReviewListResponse>>(
        API_ENDPOINTS.REVIEWS.ADMIN_LEARNING_PATH_REVIEWS(learningPathId),
        { params }
      );
      return response.data.data;
    } catch (error) {
      handleError(error, 'Failed to fetch learning path reviews');
    }
  }

  // Public Endpoints - Course


  /**
   * Get reviews for a course
   * GET /api/reviews/course/:courseId
   */
  async getCourseReviews(
    courseId: string,
    params: ReviewListParams = {}
  ): Promise<ReviewListResponse> {
    try {
      const response = await apiClient.get<ApiResponse<ReviewListResponse>>(
        API_ENDPOINTS.REVIEWS.COURSE_REVIEWS(courseId),
        { params }
      );
      return response.data.data;
    } catch (error) {
      handleError(error, 'Failed to fetch course reviews');
    }
  }

  /**
   * Get review statistics for a course
   * GET /api/reviews/course/:courseId/stats
   */
  async getCourseReviewStats(courseId: string): Promise<ReviewStats> {
    try {
      const response = await apiClient.get<ApiResponse<ReviewStats>>(
        API_ENDPOINTS.REVIEWS.COURSE_STATS(courseId)
      );
      return response.data.data;
    } catch (error) {
      handleError(error, 'Failed to fetch review statistics');
    }
  }


  // Public Endpoints - Certification

  /**
   * Get reviews for a certification
   * GET /api/reviews/certification/:certificationId
   */
  async getCertificationReviews(
    certificationId: string,
    params: ReviewListParams = {}
  ): Promise<ReviewListResponse> {
    try {
      const response = await apiClient.get<ApiResponse<ReviewListResponse>>(
        API_ENDPOINTS.REVIEWS.CERTIFICATION_REVIEWS(certificationId),
        { params }
      );
      return response.data.data;
    } catch (error) {
      handleError(error, 'Failed to fetch certification reviews');
    }
  }

  /**
   * Get review statistics for a certification
   * GET /api/reviews/certification/:certificationId/stats
   */
  async getCertificationReviewStats(certificationId: string): Promise<ReviewStats> {
    try {
      const response = await apiClient.get<ApiResponse<ReviewStats>>(
        API_ENDPOINTS.REVIEWS.CERTIFICATION_STATS(certificationId)
      );
      return response.data.data;
    } catch (error) {
      handleError(error, 'Failed to fetch certification review statistics');
    }
  }

  // Public Endpoints - Learning Path

  /**
   * Get reviews for a learning path
   * GET /api/reviews/learning-path/:learningPathId
   */
  async getLearningPathReviews(
    learningPathId: string,
    params: ReviewListParams = {}
  ): Promise<ReviewListResponse> {
    try {
      const response = await apiClient.get<ApiResponse<ReviewListResponse>>(
        API_ENDPOINTS.REVIEWS.LEARNING_PATH_REVIEWS(learningPathId),
        { params }
      );
      return response.data.data;
    } catch (error) {
      handleError(error, 'Failed to fetch learning path reviews');
    }
  }

  /**
   * Get review statistics for a learning path
   * GET /api/reviews/learning-path/:learningPathId/stats
   */
  async getLearningPathReviewStats(learningPathId: string): Promise<ReviewStats> {
    try {
      const response = await apiClient.get<ApiResponse<ReviewStats>>(
        API_ENDPOINTS.REVIEWS.LEARNING_PATH_STATS(learningPathId)
      );
      return response.data.data;
    } catch (error) {
      handleError(error, 'Failed to fetch learning path review statistics');
    }
  }

  // ==========================================================================
  // Bulk Stats - Single API call for list pages (reduces N+1 API calls)
  // ==========================================================================

  /**
   * Get bulk review statistics for all entities of a type
   * GET /api/reviews/bulk-stats?review_type=course
   *
   * Use this for LIST PAGES only. For detail pages, use individual methods:
   * - getCourseReviewStats(courseId)
   * - getCertificationReviewStats(certificationId)
   * - getLearningPathReviewStats(learningPathId)
   */
  async getBulkReviewStats(reviewType: ReviewType): Promise<Map<string, ReviewStats>> {
    try {
      const response = await apiClient.get<ApiResponse<BulkReviewStatsResponse>>(
        API_ENDPOINTS.REVIEWS.BULK_STATS,
        { params: { review_type: reviewType } }
      );

      const statsMap = new Map<string, ReviewStats>();
      const statsObj = response.data.data.stats;

      for (const [entityId, stats] of Object.entries(statsObj)) {
        statsMap.set(entityId, stats);
      }

      return statsMap;
    } catch (error) {
      console.error('Failed to fetch bulk review stats:', error);
      return new Map<string, ReviewStats>();
    }
  }

  /**
   * Get review stats for all courses (list page - 1 API call)
   */
  async getBatchCourseStats(): Promise<Map<string, ReviewStats>> {
    return this.getBulkReviewStats(ReviewType.COURSE);
  }

  /**
   * Get review stats for all certifications (list page - 1 API call)
   */
  async getBatchCertificationStats(): Promise<Map<string, ReviewStats>> {
    return this.getBulkReviewStats(ReviewType.CERTIFICATE);
  }

  /**
   * Get review stats for all learning paths (list page - 1 API call)
   */
  async getBatchLearningPathStats(): Promise<Map<string, ReviewStats>> {
    return this.getBulkReviewStats(ReviewType.LEARNING_PATH);
  }
}

export const reviewService = new ReviewService();
